import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { X, Loader2 } from "lucide-react";
import { getMicStream, releaseMic } from "@/lib/recorder";
import { MOCKS, buildExam, mixOf } from "@/data/mocks";
import { PARTS, partOf, tierRate } from "@/data/parts";
import { getBank, getSettings, saveHistoryEntry, getActiveExam, saveActiveExam, clearActiveExam } from "@/lib/bank";
import { buildReport, scoreItem } from "@/lib/scoring";
import { cancelSpeech } from "@/lib/speech";
import { useCountdown } from "@/hooks/useCountdown";
import { ItemRenderer } from "@/components/items/ItemRenderer";
import { useAuth } from "@/context/AuthContext";
import { UpgradeLock } from "@/components/AccessGates";
import { api } from "@/lib/api";
import { recordActivity, hasAnswer } from "@/lib/streak";
import { HardwareGate } from "@/components/HardwareGate";
import { takeRoomNoise } from "@/lib/micCalibration";

const PartIntro = ({ part, onBegin, resumed }) => {
  const left = useCountdown(8, true, onBegin);
  return (
    <div data-testid="part-intro" className="space-y-5 text-center">
      {resumed && <div data-testid="exam-resumed-notice" className="mx-auto w-fit rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">Exam resumed — answers so far were saved. The current item restarts.</div>}
      <div className="font-mono text-sm uppercase tracking-[0.3em] text-indigo-600">Part {part.key}</div>
      <h2 className="font-display text-3xl font-extrabold text-slate-900">{part.name}</h2>
      <p className="mx-auto max-w-lg text-slate-600">{part.instr}</p>
      <p className="font-mono text-sm text-slate-500">Starting automatically in <span data-testid="part-intro-countdown" className="font-bold text-slate-900">{left}s</span></p>
      <button data-testid="part-intro-begin-btn" onClick={onBegin} className="rounded-full bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700">Begin now</button>
    </div>
  );
};

const PartProgress = ({ current }) => (
  <div className="flex gap-1" data-testid="exam-part-progress">
    {PARTS.map((p) => {
      const ci = PARTS.findIndex((x) => x.key === current);
      const i = PARTS.findIndex((x) => x.key === p.key);
      return <div key={p.key} className={`flex h-7 w-7 items-center justify-center rounded font-mono text-xs font-bold ${i < ci ? "bg-indigo-900 text-indigo-300" : i === ci ? "bg-indigo-600 text-white" : "bg-slate-800 text-slate-500"}`}>{p.key}</div>;
    })}
  </div>
);

export default function ExamRunnerGate() {
  const { ready, hasAccess } = useAuth();
  if (!ready) return null;
  if (!hasAccess) return <div className="mx-auto max-w-2xl px-4 py-16"><div className="rounded-2xl bg-white shadow-2xl"><UpgradeLock title="Mock exams are part of the Practice Pass" testId="exam-locked" /></div></div>;
  return <HardwareGate><ExamRunner /></HardwareGate>;
}

function ExamRunner() {
  const { mockId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const mock = MOCKS.find((m) => m.id === mockId);
  const [saved] = useState(() => { const s = getActiveExam(); return s && s.mockId === mockId ? s : null; });
  const [seq] = useState(() => saved?.seq || buildExam(mockId, getBank(), getSettings().examLength === "short"));
  const [idx, setIdx] = useState(saved?.idx || 0);
  const [intro, setIntro] = useState(true);
  const results = useRef(saved?.results || []);
  const [roomNoise] = useState(() => saved?.roomNoise ?? takeRoomNoise());

  useEffect(() => {
    if (mock && seq.length) saveActiveExam({ mockId, title: mock.title, level: mock.tier, seq, idx, results: results.current, roomNoise, savedAt: new Date().toISOString() });
  }, [idx, mockId, mock, seq, roomNoise]);

  const pendingRef = useRef([]);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    getMicStream().catch(() => {});
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => { window.removeEventListener("beforeunload", h); cancelSpeech(); releaseMic(); };
  }, []);

  const curPart = seq[idx]?.part;
  useEffect(() => { if (curPart && !["A", "B", "C", "D"].includes(curPart)) releaseMic(); }, [curPart]);

  const finish = useCallback(async () => {
    setFinishing(true);
    await Promise.allSettled(pendingRef.current);
    const scored = results.current.map((r) => ({ ...r, result: scoreItem(r.part, r.item, r.response) }));
    const rep = buildReport(scored);
    const entry = { id: `ex-${Date.now().toString(36)}`, mockId, title: mock.title, level: mock.tier, mix: mixOf(scored), date: new Date().toISOString(), ...(roomNoise != null ? { roomNoise } : {}), ...rep, items: scored.map(({ part, item, response, result }) => ({ part, item, response, score: result.score })) };
    saveHistoryEntry(entry);
    clearActiveExam();
    if (user) api.post("/history", { entry_id: entry.id, payload: entry }).catch(() => {});
    nav(`/report/${entry.id}`, { replace: true });
  }, [mockId, mock, user, nav, roomNoise]);

  const onSubmit = useCallback((raw) => {
    const { part, item } = seq[idx];
    const { pending, ...response } = raw;
    const i = results.current.length;
    results.current.push({ part, item, response });
    if (pending) {
      pendingRef.current.push(pending.then((t) => {
        results.current[i] = { ...results.current[i], response: { ...response, text: t } };
        if (t.trim()) recordActivity();
        const a = getActiveExam();
        if (a && a.mockId === mockId) saveActiveExam({ ...a, results: results.current });
      }));
    } else if (hasAnswer(response)) recordActivity();
    const next = idx + 1;
    if (next >= seq.length) return finish();
    if (seq[next].part !== part) setIntro(true);
    setIdx(next);
  }, [idx, seq, finish, mockId]);

  if (finishing) return (
    <div data-testid="exam-finishing" className="mx-auto flex max-w-xl flex-col items-center gap-4 px-4 py-24 text-center">
      <Loader2 className="h-10 w-10 animate-spin text-indigo-400" />
      <h2 className="font-display text-2xl font-bold text-white">Scoring your exam…</h2>
      <p className="text-sm text-slate-400">Transcribing your spoken responses and calculating your scaled score.</p>
    </div>
  );

  if (!mock || !seq.length) return <div className="p-10 text-white">Exam not found.</div>;
  const cur = seq[idx];
  const part = partOf(cur.part);
  const partItems = seq.filter((s) => s.part === cur.part);
  const posInPart = partItems.indexOf(cur) + 1;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Exam mode · strict</div>
          <div data-testid="exam-title" className="font-display text-lg font-bold text-white">{mock.title}</div>
        </div>
        <div className="flex items-center gap-4">
          <PartProgress current={cur.part} />
          <button data-testid="exam-exit-btn" onClick={() => { if (window.confirm("Quit the exam? Your saved progress will be discarded.")) { cancelSpeech(); clearActiveExam(); nav("/mock"); } }} className="rounded-full p-2 text-slate-400 hover:bg-slate-800 hover:text-white" aria-label="Exit exam"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="mb-3 h-1 overflow-hidden rounded-full bg-slate-800"><div className="h-full bg-indigo-500 transition-[width] duration-500" style={{ width: `${(idx / seq.length) * 100}%` }} /></div>
      <div className="mb-4 flex justify-between font-mono text-xs text-slate-400">
        <span data-testid="exam-item-counter">Part {part.key} · {part.name} · Item {posInPart} of {partItems.length}</span>
        <span data-testid="exam-overall-counter">{idx + 1} / {seq.length}</span>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-950/40 sm:p-10">
        {intro ? <PartIntro key={`intro-${cur.part}`} part={part} resumed={!!saved && idx === saved.idx && idx > 0} onBegin={() => setIntro(false)} />
          : <ItemRenderer key={`${cur.item.id}-${idx}`} part={part} item={cur.item} mode="exam" rate={tierRate(cur.item.difficulty || mock.tier)} onSubmit={onSubmit} />}
      </div>
    </div>
  );
}
